import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { formatLabel } from '@/lib/format-labels';
import { exportClientProfilePDF } from '@/utils/exportClientProfilePDF';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Derive a clean page title from the current pathname */
function getPageTitle(pathname: string): string {
  const parts = pathname.replace(/^\/admin\/?/, '').split('/').filter(Boolean);
  if (!parts.length) return 'Dashboard';
  return parts
    .map(p => formatLabel(p.replace(/-/g, '_')))
    .join(' › ');
}

/** Scrape all visible <table> elements in the main content area */
function scrapeTablesFromDOM(): { headers: string[]; rows: string[][] }[] {
  const main = document.querySelector('main') || document.body;
  const tables = Array.from(main.querySelectorAll('table'));
  return tables.map(table => {
    // Headers — prefer <thead>, else first <tr>
    const headRow = table.querySelector('thead tr') || table.querySelector('tr');
    const headers = headRow
      ? Array.from(headRow.querySelectorAll('th, td')).map(th => th.textContent?.trim() || '')
      : [];

    // Body rows
    const bodyRows = Array.from(
      table.querySelector('tbody')?.querySelectorAll('tr') || table.querySelectorAll('tr')
    ).slice(headRow ? 0 : 1); // skip header row if no thead

    const rows = bodyRows.map(tr =>
      Array.from(tr.querySelectorAll('td, th')).map(td => td.textContent?.trim() || '')
    );

    return { headers, rows };
  });
}

/** Scrape key-value pairs from labeled cards/rows (dl, .grid, etc.) */
function scrapeKeyValueFromDOM(): { key: string; value: string }[] {
  const main = document.querySelector('main') || document.body;
  const result: { key: string; value: string }[] = [];

  // dl / dt+dd pairs
  main.querySelectorAll('dl').forEach(dl => {
    const dts = Array.from(dl.querySelectorAll('dt'));
    const dds = Array.from(dl.querySelectorAll('dd'));
    dts.forEach((dt, i) => {
      result.push({ key: dt.textContent?.trim() || '', value: dds[i]?.textContent?.trim() || '' });
    });
  });

  return result;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function GlobalExportButton() {
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [loading, setLoading] = useState<'excel' | 'pdf' | 'client-pdf' | null>(null);

  // Detect if we're on a client detail page
  const clientDetailMatch = location.pathname.match(/^\/admin\/clients\/([0-9a-f-]{36})/i);
  
  const clientId = clientDetailMatch?.[1] || null;

  // Derive the export permission key from the route
  const section = location.pathname.replace(/^\/admin\/?/, '').split('/')[0] || 'dashboard';
  const exportPermKey = `${section}.export`;
  const canExport = hasPermission(exportPermKey) || hasPermission('data.export');

  if (!canExport) return null;

  const pageTitle = getPageTitle(location.pathname);
  const timestamp = format(new Date(), 'yyyy-MM-dd HH:mm');
  const fileSlug = pageTitle.replace(/\s›\s/g, '-').replace(/\s/g, '-').toLowerCase();

  // ── Client Profile PDF export ───────────────────────────────────────────────
  const exportClientPDF = async () => {
    if (!clientId) return;
    setLoading('client-pdf');
    try {
      await exportClientProfilePDF(clientId);
      toast({ title: 'Client report exported', description: 'Comprehensive PDF report downloaded' });
    } catch (err) {
      console.error('Client PDF export error:', err);
      toast({ title: 'Export failed', description: String(err), variant: 'destructive' });
    } finally {
      setLoading(null);
    }
  };

  // ── Excel export ────────────────────────────────────────────────────────────
  const exportExcel = async () => {
    setLoading('excel');
    try {
      const wb = XLSX.utils.book_new();
      const tables = scrapeTablesFromDOM();

      if (tables.length === 0) {
        // Fallback: key-value sheet
        const kvData = scrapeKeyValueFromDOM();
        if (kvData.length > 0) {
          const ws = XLSX.utils.json_to_sheet(kvData);
          XLSX.utils.book_append_sheet(wb, ws, 'Details');
        } else {
          toast({ title: 'Nothing to export', description: 'No table data found on this page.', variant: 'destructive' });
          return;
        }
      } else {
        tables.forEach((t, i) => {
          if (t.rows.length === 0 && t.headers.length === 0) return;
          const data = t.headers.length > 0
            ? [t.headers, ...t.rows]
            : t.rows;
          const ws = XLSX.utils.aoa_to_sheet(data);
          XLSX.utils.book_append_sheet(wb, ws, `Sheet${i + 1}`);
        });
      }

      XLSX.writeFile(wb, `${fileSlug}-${format(new Date(), 'yyyyMMdd')}.xlsx`);
      toast({ title: 'Excel exported', description: `${pageTitle} — ${timestamp}` });
    } catch (err) {
      console.error('Excel export error:', err);
      toast({ title: 'Export failed', description: String(err), variant: 'destructive' });
    } finally {
      setLoading(null);
    }
  };

  // ── PDF export ──────────────────────────────────────────────────────────────
  const exportPDF = async () => {
    setLoading('pdf');
    try {
      const doc = new jsPDF({ orientation: 'landscape' });
      const pageW = doc.internal.pageSize.getWidth();

      // Header bar
      doc.setFillColor(15, 23, 42); // dark navy
      doc.rect(0, 0, pageW, 18, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Paynovus Admin Portal', 14, 11);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(pageTitle, pageW / 2, 11, { align: 'center' });
      doc.text(timestamp, pageW - 14, 11, { align: 'right' });

      let yPos = 26;
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(pageTitle, 14, yPos);
      yPos += 8;

      const tables = scrapeTablesFromDOM();

      if (tables.length === 0) {
        const kvData = scrapeKeyValueFromDOM();
        if (kvData.length > 0) {
          autoTable(doc, {
            startY: yPos,
            head: [['Field', 'Value']],
            body: kvData.map(kv => [kv.key, kv.value]),
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [245, 247, 250] },
            margin: { left: 14, right: 14 },
          });
        } else {
          doc.setFontSize(10);
          doc.setTextColor(120, 120, 120);
          doc.text('No table data found on this page.', 14, yPos + 10);
        }
      } else {
        tables.forEach((t, i) => {
          if (t.rows.length === 0) return;
          if (i > 0) {
            doc.addPage();
            yPos = 14;
          }
          autoTable(doc, {
            startY: yPos,
            head: t.headers.length > 0 ? [t.headers] : undefined,
            body: t.rows,
            styles: { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak' },
            headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [245, 247, 250] },
            margin: { left: 14, right: 14 },
            didDrawPage: (_data) => {
              // Footer on each page
              const pg = doc.internal.pages.length - 1;
              doc.setFontSize(7);
              doc.setTextColor(160, 160, 160);
              doc.text(
                `Page ${pg} · Exported ${timestamp} · Paynovus Confidential`,
                pageW / 2,
                doc.internal.pageSize.getHeight() - 6,
                { align: 'center' }
              );
            },
          });
          yPos = (doc as any).lastAutoTable?.finalY + 10 || yPos + 10;
        });
      }

      doc.save(`${fileSlug}-${format(new Date(), 'yyyyMMdd')}.pdf`);
      toast({ title: 'PDF exported', description: `${pageTitle} — ${timestamp}` });
    } catch (err) {
      console.error('PDF export error:', err);
      toast({ title: 'Export failed', description: String(err), variant: 'destructive' });
    } finally {
      setLoading(null);
    }
  };

  // On client profile pages, single-click directly downloads the full report PDF
  if (clientId) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 text-xs border-border/60 text-muted-foreground hover:text-foreground"
        disabled={!!loading}
        onClick={exportClientPDF}
      >
        {loading === 'client-pdf' ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Download className="h-3.5 w-3.5" />
        )}
        <span className="hidden sm:inline">Export Report</span>
      </Button>
    );
  }

  // For other pages, show dropdown with Excel/PDF options
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs border-border/60 text-muted-foreground hover:text-foreground"
          disabled={!!loading}
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          <span className="hidden sm:inline">Export</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          {pageTitle}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={exportExcel}
          disabled={loading === 'excel'}
          className="gap-2 cursor-pointer"
        >
          {loading === 'excel' ? (
            <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
          ) : (
            <FileSpreadsheet className="h-4 w-4 text-emerald-500" />
          )}
          <div>
            <div className="text-sm font-medium">Export to Excel</div>
            <div className="text-xs text-muted-foreground">.xlsx format</div>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={exportPDF}
          disabled={loading === 'pdf'}
          className="gap-2 cursor-pointer"
        >
          {loading === 'pdf' ? (
            <Loader2 className="h-4 w-4 animate-spin text-destructive" />
          ) : (
            <FileText className="h-4 w-4 text-destructive" />
          )}
          <div>
            <div className="text-sm font-medium">Export to PDF</div>
            <div className="text-xs text-muted-foreground">.pdf format, landscape</div>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
